import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React from 'react';
import { Link } from 'react-router-dom';
import GenericTable, {
  GTColumnSelectAnchor,
  GTExpander,
  GTFrameworkCell,
  GTModifierLabel,
} from 'src/components/GenericTable';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { formatDate, formatNumber, getPercentage } from 'src/utils/misc';
import { filter } from 'lodash';
import { getIncompleteStateNew, TextOverflowTooltip, TableSkeleton } from 'src/common';
import { checkIsModifierStrategy } from 'src/components/Modifier/util';
import useVersion from 'src/hooks/useVersion';
import { encodeQueryParams } from 'src/api/client';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { Modifier } from 'src/components/Modifier';
import { convertToModifiersGql } from 'src/components/ProjectedRisk/util';
import { OpenInNew } from '@mui/icons-material';
import { AssessmentQuery, useScenarioRmpQuery } from 'src/graphql/_generated';
import { getMaturityLevelName } from 'src/api/types';

interface ScenarioTableExpandContentProps {
  scenarioId: string;
  scenario: NonNullable<AssessmentQuery['assessment']>['scenarios'][number];
  assessmentId: string;
}

const defaultColumns = ['strategy', 'name', 'owner', 'status', 'updated_at', '_mod_applied'];
const internalIds = [GTInternalIds.expander, GTInternalIds.tdm];

export const ScenarioTableExpandContent: React.FC<ScenarioTableExpandContentProps> = ({
  scenarioId,
  scenario,
  assessmentId,
}) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.assessmentScenariosControls}-columns`,
    defaultColumns,
  );
  const { version } = useVersion();

  const { data: scenarioRmpData } = useScenarioRmpQuery({ variables: { id: scenarioId } });

  const data =
    scenarioRmpData?.scenario?.riskManagementPlans
      .map((el) => ({
        control: el.control!,
        modifiers: el.modifiers,
        starting_frequency: el.startingFrequency || 0,
        starting_lower: el.startingLower || 0,
        starting_upper: el.startingUpper || 0,
      }))
      .filter((el) => el.control?.name) || [];

  const columns: ColumnDef<(typeof data)[number]>[] = [
    {
      id: GTInternalIds.expander,
      header: () => null,
      cell: ({ row }) => {
        return row.getCanExpand() ? (
          <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
        ) : null;
      },
      size: 1,
      maxSize: 20,
      enableSorting: false,
    },
    {
      id: 'ux_id',
      header: 'Id',
      accessorFn: (row) => row.control?.id,
      cell: (info) => info.getValue(),
    },
    {
      id: 'strategy',
      header: 'Strategy',
      accessorFn: (row) => row.control?.strategy,
      cell: (info) => info.getValue(),
    },
    {
      id: 'name',
      header: 'Control',
      accessorFn: (row) => row.control?.name,
      cell: (info) => (
        <TextOverflowTooltip>
          <Link
            className="table-link"
            to={`/risk-management/control/${info.row.original.control?.id}${
              version ? encodeQueryParams({ version, assessmentId }) : ''
            }`}
          >
            {info.getValue() as string}
          </Link>
        </TextOverflowTooltip>
      ),
      sortingFn: sortingFns.text,
    },
    {
      id: 'owner',
      header: 'Owner',
      accessorFn: (row) => row.control?.ownerName,
      cell: (info) => info.getValue(),
      sortingFn: sortingFns.text,
    },
    {
      id: 'framework_controls',
      header: 'Framework',
      cell: (info) => {
        const control = info.row.original.control;
        return <GTFrameworkCell controls={control?.frameworkControls || []} />;
      },
      sortingFn: sortingFns.text,
    },
    {
      id: 'annual_cost',
      header: 'Annual Cost',
      accessorFn: (row) => row.control?.annualCost,
      cell: ({ row }) => formatNumber(row.original.control?.annualCost, { symbol: true }),
      sortingFn: sortingFns.alphanumeric,
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row.control?.status,
      cell: (info) => info.getValue(),
    },
    {
      id: 'existingMaturity',
      header: 'Existing Maturity',
      cell: ({ row }) => getMaturityLevelName(row.original.control?.existingMaturity),
    },
    {
      id: 'targetMaturity',
      header: 'Target Maturity',
      cell: ({ row }) => getMaturityLevelName(row.original.control?.targetMaturity),
    },
    {
      id: 'updated_at',
      header: 'Last Edited',
      accessorFn: (row) => row.control?.meta?.updatedAt || null,
      cell: ({ row }) => formatDate(row.original.control?.meta?.updatedAt, { formatType: 'datetime' }),
    },
    {
      id: '_mod_applied',
      header: 'Modifiers',
      accessorFn: (row) => row.modifiers?.length || 0,
      cell: (info) => {
        const isModStrategy = checkIsModifierStrategy(info.row.original.control?.strategy);

        if (!isModStrategy) return null;
        return (
          <GTModifierLabel
            length={info.getValue() as number}
            satisfiesModifier={getIncompleteStateNew(scenario) !== 'TECH'}
            plainText
          />
        );
      },
    },
    {
      id: 'source',
      header: 'Link',
      accessorFn: (row) => row.control?.linkUrl || null,
      cell: (info) => {
        const url = info.getValue();
        const linkText = (info.row.original.control?.linkText || url) as string;

        return url ? (
          <a
            href={url as string}
            target="_blank"
            rel="noopener noreferrer"
            className="table-link"
            onClick={(e) => e.stopPropagation()}
          >
            {linkText}{' '}
            <OpenInNew
              css={`
                font-size: 14px !important;
                color: inherit;
              `}
            />
          </a>
        ) : (
          <span>{linkText}</span>
        );
      },
    },
    {
      id: GTInternalIds.tdm,
      header: () => null,
      cell: () => null,
      size: 1,
    },
    // {
    //   id: '_mod_applied',
    //   header: 'Modifiers Applied',
    //   accessorFn: (row) => (row.modifiers?.treat.length || 0) + (row.modifiers?.transfer.length || 0),
    //   cell: (info) => {
    //     console.log(info.row.original);
    //     const length = info.getValue();
    //     return `${length} Modifier${length === 1 ? '' : 's'}`;
    //   },
    // },
  ];

  if (!scenarioRmpData) return <TableSkeleton />;

  return (
    <GenericTable
      tableId={TableIds.assessmentScenariosControls}
      data={data}
      columns={filter(columns, (el) => [...internalIds, ...selectedColumns].includes(el.id as string))}
      GTColumnSelectAnchorExported={
        <GTColumnSelectAnchor
          tableId={TableIds.assessmentScenariosControls}
          options={[
            { key: 'ux_id', title: 'Id' },
            { key: 'strategy', title: 'Strategy' },
            { key: 'name', title: 'Control' },
            { key: 'owner', title: 'Owner' },
            { key: 'framework_controls', title: 'Framework' },
            { key: 'annual_cost', title: 'Annual Cost' },
            { key: 'status', title: 'Status' },
            { key: 'existingMaturity', title: 'Existing Maturity' },
            { key: 'targetMaturity', title: 'Target Maturity' },
            { key: 'updated_at', title: 'Last Edited' },
            { key: '_mod_applied', title: 'Modifiers' },
            { key: 'source', title: 'Link' },
          ]}
          defaultOptions={defaultColumns}
          value={selectedColumns}
          onChange={setSelectedColumns}
        />
      }
      expandContent={(el) => {
        const modsArr = el.modifiers;

        if (!modsArr.length) return null;
        const modifiers = convertToModifiersGql(modsArr);

        return (
          <Modifier
            control={{ ...el.control, modifiers }}
            projectedScenario={{
              id: scenario.id,
              upper: scenario.liveRisk?.upper || 0,
              lower: scenario.liveRisk?.lower || 0,
              frequency_years: scenario.liveRisk?.frequencyYears || 0,
              frequency_times: getPercentage(
                scenario.liveRisk?.frequencyTimes || 0,
                scenario.liveRisk?.frequencyYears || 0,
              ),
            }}
            startingValues={{
              frequency_times: el.starting_frequency * 100,
              lower: el.starting_lower,
              upper: el.starting_upper,
            }}
            disabled
            noResult
          />
        );
      }}
      darkMode
    />
  );
};
